import { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Icon, { TYPE_META } from "@/components/Icon";
import LinkThumb from "@/components/LinkThumb";
import AdminPanel from "@/components/AdminPanel";
import { LogOut, Search, Settings, ArrowUpRight, Shield, NotebookPen } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "website", label: "Websites" },
  { key: "webapp", label: "Web Apps" },
  { key: "mobile", label: "Mobile" },
  { key: "drive", label: "Drives" },
  { key: "tool", label: "Tools" },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [links, setLinks] = useState([]);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [adminOpen, setAdminOpen] = useState(false);

  const load = useCallback(async () => {
    const [c, l] = await Promise.all([api.get("/categories"), api.get("/links")]);
    setCategories(c.data);
    setLinks(l.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openLink = (link) => {
    if (link.url.startsWith("internal:")) {
      if (user.role === "admin") {
        setAdminOpen(true);
      } else {
        toast.error("Manager access required for this tool");
      }
      return;
    }
    window.open(link.url, "_blank", "noopener,noreferrer");
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return links.filter((l) => {
      const okType = filter === "all" || l.type === filter;
      const okQ =
        !q ||
        l.title.toLowerCase().includes(q) ||
        (l.description || "").toLowerCase().includes(q);
      return okType && okQ;
    });
  }, [links, filter, query]);

  return (
    <div className="relative min-h-screen bg-[#F8F9FA] text-slate-900">
      <div className="noise-overlay" />

      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-white/50 bg-white/60 backdrop-blur-2xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 text-white">
              <Shield className="h-4 w-4" />
            </div>
            <div>
              <p className="font-display text-lg leading-none">Nexus</p>
              <p className="font-plex text-[11px] text-slate-400">
                Global Manager Portal
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden items-center gap-2 rounded-full border border-white/60 bg-white/70 px-4 py-2 md:flex">
              <Search className="h-4 w-4 text-slate-400" />
              <input
                data-testid="search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search links…"
                className="w-40 bg-transparent font-plex text-sm outline-none placeholder:text-slate-300"
              />
            </div>
            {user?.role === "admin" && (
              <button
                data-testid="open-admin-btn"
                onClick={() => setAdminOpen(true)}
                className="flex items-center gap-2 rounded-full border border-white/60 bg-white/70 px-4 py-2 font-plex text-sm transition-colors hover:bg-white"
              >
                <Settings className="h-4 w-4" /> Manage
              </button>
            )}
            <button
              data-testid="open-register-btn"
              onClick={() => navigate("/register")}
              className="flex items-center gap-2 rounded-full border border-white/60 bg-white/70 px-4 py-2 font-plex text-sm transition-colors hover:bg-white"
            >
              <NotebookPen className="h-4 w-4" /> Register
            </button>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 font-plex text-sm text-white transition-transform hover:-translate-y-0.5"
            >
              <LogOut className="h-4 w-4" /> Exit
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 pb-24 pt-14">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10"
        >
          <p className="font-plex text-sm text-slate-400">
            {new Date().toLocaleDateString(undefined, {
              weekday: "long",
              month: "long",
              day: "numeric",
            })}
          </p>
          <h1 className="mt-1 font-display text-4xl sm:text-5xl">
            Welcome back, {user?.name || user?.username}.
          </h1>
        </motion.div>

        {/* Filters */}
        <div className="mb-10 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`rounded-full border px-4 py-1.5 font-plex text-sm transition-colors ${
                filter === f.key
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-white/60 bg-white/60 text-slate-500 hover:bg-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
          {categories.map((cat, ci) => {
            const catLinks = filtered.filter((l) => l.category_id === cat.id);
            if (catLinks.length === 0) return null;
            return (
              <motion.section
                key={cat.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: ci * 0.06 }}
                className={`${
                  cat.span >= 2 ? "md:col-span-4 lg:col-span-3" : "md:col-span-2 lg:col-span-1"
                } rounded-3xl border border-white/60 bg-white/60 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] backdrop-blur-2xl`}
              >
                <div className="mb-6 flex items-center gap-3">
                  <div
                    className="flex h-10 w-10 items-center justify-center rounded-xl text-white"
                    style={{ backgroundColor: cat.accent }}
                  >
                    <Icon name={cat.icon} className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="font-heading text-lg font-semibold">
                      {cat.name}
                    </h2>
                    <p className="font-plex text-xs text-slate-400">
                      {cat.description}
                    </p>
                  </div>
                </div>

                <div className={`grid grid-cols-1 gap-3 ${cat.span >= 2 ? "sm:grid-cols-2" : ""}`}>
                  {catLinks.map((link, li) => {
                    const meta = TYPE_META[link.type] || TYPE_META.other;
                    return (
                      <motion.button
                        key={link.id}
                        data-testid={`link-${link.id}`}
                        onClick={() => openLink(link)}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: li * 0.04 }}
                        className="group flex items-start gap-3 rounded-2xl border border-transparent bg-white/50 p-4 text-left transition-all duration-200 hover:-translate-y-1 hover:border-white/80 hover:bg-white hover:shadow-lg"
                      >
                        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600 group-hover:bg-slate-900 group-hover:text-white transition-colors">
                          <LinkThumb link={link} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1">
                            <p className="truncate font-plex text-sm font-medium text-slate-900">
                              {link.title}
                            </p>
                            <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-slate-300 opacity-0 transition-opacity group-hover:opacity-100" />
                          </div>
                          <p className="truncate font-plex text-xs text-slate-400">
                            {link.description || meta.label}
                          </p>
                          <span className="mt-1.5 inline-block rounded-full bg-slate-100 px-2 py-0.5 font-plex text-[10px] uppercase tracking-wider text-slate-400">
                            {meta.label}
                          </span>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </motion.section>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <p className="mt-16 text-center font-plex text-slate-400">
            No links match your filter.
          </p>
        )}
      </main>

      {user?.role === "admin" && (
        <AdminPanel
          open={adminOpen}
          onOpenChange={setAdminOpen}
          categories={categories}
          links={links}
          onChanged={load}
        />
      )}
    </div>
  );
}
